import { tz } from '@date-fns/tz';
import { format, parseISO } from 'date-fns';

/**
 * Duration expressed as a fractional hour count → a `"HH:mm"` clock label.
 * Used only by timeline scale rulers where the input is an integer / decimal
 * hour offset (0..24), not a timestamp. Pure math — no date library needed.
 */
export const formatNumberToHours = (hourNumber) => {
	const totalMinutes = Math.round(hourNumber * 60);
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes - hours * 60;
	return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const toDate = (input) => (typeof input === 'string' ? parseISO(input) : input);

/**
 * ISO-shape "HH:mm" string. **For `<input type="time">` values only** — the
 * HTML5 time input requires a fixed 24-hour "HH:mm" payload regardless of the
 * user's locale (the browser decides how to render it back to the user;
 * en-US Chrome shows an AM/PM picker over the same value that de-DE Chrome
 * shows as 24-hour).
 *
 * For any user-facing display of a clock reading, use `formatTime` — it
 * follows `navigator.language` (en-US → 12h, other → 24h). Do not use this
 * function for display text, and do not use `formatTime` for input values.
 */
export const formatHoursMinutesTime = (date, timezone) => {
	const options = timezone ? { in: tz(timezone) } : undefined;
	return format(toDate(date), 'HH:mm', options);
};

/**
 * ISO date `"YYYY-MM-DD"` string for `<input type="date">` values. Same
 * "locale-independent shape" rule as `formatHoursMinutesTime`.
 */
export const getDateToEdit = (date, timezone) => {
	const options = timezone ? { in: tz(timezone) } : undefined;
	return format(toDate(date), 'yyyy-MM-dd', options);
};
